/**
 * @file  audit/index.ts
 * @description  audit service to track activity and events
 *
 * @author ogarcia (ozkary)
 *
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ModalManager from '../../components/ModalManager';
import { Audit, AuditStatus } from '../../functions/audit/audit.model';
import { clearContractIfCompleted } from '../../services/contract';
import { isRouteRestricted } from '../../routes/baseConfig';
import { hasRenewedContract, hasContracted } from '../../services/customer';
import {
  ModalManagerState,
  ModalMessages
} from '../../components/ModalManager/styles';
import { ContentLoader } from './content';
import * as _log from '../../services/logger';
import { getAudit, initializeAudit, track } from '../../services/audit';

/**
 * audit component to handle navigation events including timeout, abandonment, complete
 * prevent back navigation
 */
const AuditTracker = (): JSX.Element => {
  const history = useHistory();
  // const [viewIntent, setViewIntent] = useState(0);
  const [modalState, setModalState] = useState<ModalManagerState>({ name: '' });
  const [modalMessages, setContent] = useState<ModalMessages>(
    {} as ModalMessages
  );

  /**
   * loads the audit messages
   */
  const loadContent = useCallback(async () => {
    const content = await ContentLoader;
    setContent(content);
  }, []);

  /**
   * audit the current visit state to make sure certain data is reset or exists
   * to allow the user to visit that page
   */
  const auditCurrentVisit = useCallback(async () => {
    const current = history.location.pathname;
    // for complete visits reset the contract information
    if (current !== '/receipt') {
      clearContractIfCompleted();
    }
    // for renewal visits, the user should not land on other pages after purchased.
    if (await isRouteRestricted(current)) {
      if (await hasRenewedContract()) {
        history.push('/end');
        _log.track('contract-renewed', false);
      }
    }
  }, []);

  /**
   * Global error handler with special handling for chunk load errors
   * @param ev error event
   * @param source
   * @param lineNo
   * @param columnNo
   */
  const errorHandler = function (
    ev: ErrorEvent
    /* source?: string,
    lineNo?: number,
    columnNo?: number */
  ): void {
    console.log(ev);
    const err = ev.error;
    if (err) {
      _log.error(err);

      const msg = err.message.toLowerCase();
      // detect the chunkload error
      const msgTag = 'chunkloaderror';
      if (msg && msg.indexOf(msgTag) > -1) {
        const info = modalMessages[msgTag];
        if (info) {
          _log.audit<string>(msgTag, info?.title);
          alert(info?.content); // use the browser alert
        }
      }
    }
  };

  /**
   * log the state of the application
   */
  const log = useCallback(async (): Promise<void> => {
    const page = history.location.pathname;
    const auditCache = await getAudit();
    const audit = auditCache || initializeAudit();

    if (audit.pageView && !audit.pageView.includes(page)) {
      audit.pageView.push(page);
    }

    // ga tracking
    _log.track(page);

    let activity: Audit | undefined = { ...audit };
    const status = page !== '/' ? page.replace('/', '') : AuditStatus.visit;

    if (
      activity.status === AuditStatus.complete &&
      status !== activity.status
    ) {
      // this is post complete page load. reset the audit to start new
      activity = undefined;
    } else {
      activity.status = status;
    }

    track(activity);
  }, []);

  /**
   * initialize the event listeners - single time
   */
  useEffect(() => {
    loadContent();
    auditCurrentVisit();
    log();

    /**
     * prevent the
     * @param ev back event
     */
    window.onpopstate = function (ev: PopStateEvent) {
      ev?.stopImmediatePropagation();
      history.go(1);
    };

    /**
     * displays the leave intent only after the user cancel the browser and the page gets the focus back
     * window. Focus helps us determine if the user interacted with the modal dialog
     */
    const showFocusIntent = async () => {
      setModalState({ name: 'leaveIntent' });
      window.removeEventListener('focus', showFocusIntent);
    };

    /**
     * Shows the leave intent only one time
     * @param e unload event
     */
    const showLeaveIntent = async (e: BeforeUnloadEvent) => {
      const status = await hasContracted();
      if (!status) {
        e.preventDefault();
        e.returnValue = '';
        _log.track('leave-intent');
        // add the one time listeners
        window.addEventListener('focus', showFocusIntent);
        window.removeEventListener('beforeunload', showLeaveIntent);
      }
    };

    /**
     * track the unload event
     */
    window.addEventListener('beforeunload', showLeaveIntent);

    /**
     * listen to location changes
     */
    history.listen(async () => {
      auditCurrentVisit();
      log();
      // push the new location. current visited page to prevent a back operation
      window.history.pushState(null, '', location.href);
    });

    /**
     * add the global error handler with tracking
     */
    window.addEventListener('error', errorHandler);
  }, []);

  return <ModalManager messages={modalMessages} state={modalState} />;
};

export default AuditTracker;
